import type { PiiSpan } from "./types";
import { detect } from "./detect";
import type { PiiWorkerRequest, PiiWorkerResponse } from "./piiScanWorker";

/**
 * Main-thread facade over the PII scan worker (LAR-27 v2).
 *
 * The worker is a long-lived singleton because it owns the ScandiNER model:
 * `prewarmPiiScan()` is called once at app boot so the ~178 MB download +
 * session build happen long before the first send; the send-gate only ever
 * waits on a cold cache (rare — decision 5), surfacing the persistent
 * "forbereder sikkerhedstjek…" indicator via `isPiiScanReady`/`onPiiScanReady`.
 *
 * Fail-closed contract (decisions 4 & 5): the promise resolves with spans only
 * when a FULL scan (regex + NER) completed by some path — worker first, then a
 * one-shot main-thread fallback (dynamic import keeps transformers.js out of
 * the main bundle). If both fail, it REJECTS and the caller must block the
 * send; there is deliberately no regex-only "partial" result.
 */

/** Upper bound for model load (download on cold cache) before failing closed. */
const PREWARM_TIMEOUT_MS = 120_000;
/** Upper bound for a single scan once the model is warm. */
const SCAN_TIMEOUT_MS = 20_000;

let worker: Worker | null = null;
let workerBroken = false;
let ready = false;
let prewarmPromise: Promise<void> | null = null;

let nextScanId = 1;
const pendingScans = new Map<
  number,
  { resolve: (spans: PiiSpan[]) => void; reject: (err: Error) => void }
>();

type ReadyListener = () => void;
const readyListeners = new Set<ReadyListener>();

let resolvePrewarm: (() => void) | null = null;
let rejectPrewarm: ((err: Error) => void) | null = null;

function markReady(): void {
  ready = true;
  resolvePrewarm?.();
  resolvePrewarm = null;
  rejectPrewarm = null;
  for (const listener of readyListeners) listener();
}

function failAllPending(message: string): void {
  for (const { reject } of pendingScans.values()) reject(new Error(message));
  pendingScans.clear();
}

function handleWorkerMessage(e: MessageEvent<PiiWorkerResponse>): void {
  const msg = e.data;
  switch (msg.type) {
    case "ready":
      markReady();
      break;
    case "prewarm-error":
      workerBroken = true;
      rejectPrewarm?.(new Error(msg.message));
      resolvePrewarm = null;
      rejectPrewarm = null;
      break;
    case "result":
      pendingScans.get(msg.id)?.resolve(msg.spans);
      pendingScans.delete(msg.id);
      break;
    case "scan-error":
      pendingScans.get(msg.id)?.reject(new Error(msg.message));
      pendingScans.delete(msg.id);
      break;
  }
}

function getWorker(): Worker | null {
  if (workerBroken) return null;
  if (worker) return worker;
  try {
    // Vite bundles the worker as a module via `new URL(..., import.meta.url)`.
    worker = new Worker(new URL("./piiScanWorker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = handleWorkerMessage;
    worker.onerror = () => {
      workerBroken = true;
      rejectPrewarm?.(new Error("PII scan worker crashed"));
      resolvePrewarm = null;
      rejectPrewarm = null;
      failAllPending("PII scan worker crashed");
      worker?.terminate();
      worker = null;
    };
    return worker;
  } catch {
    workerBroken = true;
    return null;
  }
}

/** Main-thread fallback: load ScandiNER here (dynamic import) and scan inline. */
async function mainThreadScan(
  text: string,
  whitelist: string[]
): Promise<PiiSpan[]> {
  const { detectNer } = await import("./ner");
  const nerSpans = await detectNer(text);
  return detect(text, { whitelist: new Set(whitelist), nerSpans });
}

async function mainThreadPrewarm(): Promise<void> {
  const { prewarmNer } = await import("./ner");
  await prewarmNer();
  markReady();
}

/**
 * Kick off (or join) the one-time model load. Call at app boot. Resolves when
 * scans are warm; rejects if the model cannot be loaded by worker OR fallback
 * (the send-gate then fails closed per scan instead).
 */
export function prewarmPiiScan(): Promise<void> {
  if (prewarmPromise) return prewarmPromise;

  prewarmPromise = new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      rejectPrewarm = null;
      resolvePrewarm = null;
      // A timed-out worker load is just another worker-path failure: retire
      // the worker like the crash path does (else scans keep preferring the
      // wedged worker and eat the scan timeout per send), then try the main
      // thread once before giving up.
      workerBroken = true;
      failAllPending("PII scan worker timed out");
      worker?.terminate();
      worker = null;
      mainThreadPrewarm().then(
        () => resolve(),
        () => reject(new Error("PII model load timed out"))
      );
    }, PREWARM_TIMEOUT_MS);

    const settle = {
      resolve: () => {
        clearTimeout(timer);
        resolve();
      },
      reject: (err: Error) => {
        clearTimeout(timer);
        // Worker path failed → try the main thread once before giving up.
        mainThreadPrewarm().then(settle.resolve, () => reject(err));
      },
    };

    const w = getWorker();
    if (!w) {
      mainThreadPrewarm().then(settle.resolve, reject);
      return;
    }
    resolvePrewarm = settle.resolve;
    rejectPrewarm = settle.reject;
    w.postMessage({ type: "prewarm" } satisfies PiiWorkerRequest);
  }).catch((err: unknown) => {
    prewarmPromise = null; // allow a retry on the next call
    throw err;
  });

  return prewarmPromise;
}

/** True once the model is warm (scans return in ~100ms). */
export function isPiiScanReady(): boolean {
  return ready;
}

/** Subscribe to the ready signal (drives the "forbereder…" indicator). */
export function onPiiScanReady(listener: ReadyListener): () => void {
  if (ready) listener();
  else readyListeners.add(listener);
  return () => readyListeners.delete(listener);
}

/**
 * Scan a prompt for PII (regex + ScandiNER + coref/numbering). Waits for the
 * model if it is still loading. Rejects ONLY when no full scan is possible —
 * the caller (the chat send-gate) must treat a rejection as "block the send",
 * never as "send un-scanned".
 */
export async function scanPrompt(
  text: string,
  whitelist: string[] = []
): Promise<PiiSpan[]> {
  await prewarmPiiScan();

  const w = getWorker();
  if (!w) return mainThreadScan(text, whitelist);

  const viaWorker = new Promise<PiiSpan[]>((resolve, reject) => {
    const id = nextScanId++;
    const timer = setTimeout(() => {
      pendingScans.delete(id);
      reject(new Error("PII scan timed out"));
    }, SCAN_TIMEOUT_MS);
    pendingScans.set(id, {
      resolve: (spans) => {
        clearTimeout(timer);
        resolve(spans);
      },
      reject: (err) => {
        clearTimeout(timer);
        reject(err);
      },
    });
    w.postMessage({
      type: "scan",
      id,
      text,
      whitelist,
    } satisfies PiiWorkerRequest);
  });

  try {
    return await viaWorker;
  } catch {
    // One-shot fallback; if this also throws, the rejection IS the fail-closed
    // signal for the caller.
    return mainThreadScan(text, whitelist);
  }
}
